import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resolveTenant, type TenantErrorCode } from './resolveTenant';
import type { Restaurant } from '@shared/types/restaurant';
import { safeSessionStorage } from '@shared/utils/safeStorage';

const MEITRE_REDIRECT_URL = import.meta.env.DEV
  ? 'http://localhost:8080'
  : 'https://meitre.com';

interface TenantContextValue {
  /** The resolved restaurant data, or null if loading/error */
  restaurant: Restaurant | null;
  /** The subdomain prefix from the URL */
  subdomainPrefix: string | null;
  /** The API base URL for this tenant */
  apiBaseUrl: string | null;
  /** Whether tenant resolution is in progress */
  isLoading: boolean;
  /** Error message if resolution failed */
  error: string | null;
  /** Error code for programmatic handling */
  errorCode: TenantErrorCode | null;
  /** Function to refetch tenant data */
  refetch: () => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: React.ReactNode;
  /**
   * If true, redirects to meitre.com on error instead of showing error state.
   * Default: true in production, false in development
   */
  redirectOnError?: boolean;
}

export function TenantProvider({
  children,
  redirectOnError = import.meta.env.PROD,
}: TenantProviderProps) {
  const [hasRedirected, setHasRedirected] = useState(false);

  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['tenant'],
    queryFn: resolveTenant,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });

  // Handle redirect on error (only for fatal errors, not network issues)
  useEffect(() => {
    if (!isLoading && !hasRedirected && redirectOnError) {
      const shouldRedirect =
        data?.errorCode === 'NO_SUBDOMAIN' ||
        data?.errorCode === 'RESTAURANT_NOT_FOUND' ||
        data?.errorCode === 'RESTAURANT_INACTIVE';

      if (shouldRedirect) {
        setHasRedirected(true);
        window.location.href = MEITRE_REDIRECT_URL;
      }
    }
  }, [isLoading, data, redirectOnError, hasRedirected]);

  useEffect(() => {
    if (data?.restaurant?.isoCountryCode) {
      safeSessionStorage.setItem('restaurant_country', data.restaurant.isoCountryCode);
    }
    if (data?.restaurant?.countryCode) {
      safeSessionStorage.setItem('restaurant_dial_code', String(data.restaurant.countryCode));
    }
  }, [data?.restaurant?.isoCountryCode, data?.restaurant?.countryCode]);

  const contextValue: TenantContextValue = {
    restaurant: data?.restaurant ?? null,
    subdomainPrefix: data?.subdomainPrefix ?? null,
    apiBaseUrl: data?.apiBaseUrl ?? null,
    isLoading,
    error: data?.error ?? (queryError instanceof Error ? queryError.message : null),
    errorCode: (data?.errorCode as TenantErrorCode) ?? ((queryError as { code?: string })?.code as TenantErrorCode) ?? null,
    refetch: () => refetch(),
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant/restaurant data.
 * Must be used within TenantProvider.
 *
 * @throws Error if used outside TenantProvider
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}

/**
 * Hook to get just the restaurant, throws if not available.
 * Useful when you're certain the restaurant is loaded (e.g., inside TenantGuard).
 *
 * @throws Error if restaurant is not available
 */
export function useRestaurant(): Restaurant {
  const { restaurant, isLoading, error } = useTenant();

  if (isLoading) {
    throw new Error('Restaurant data is still loading');
  }

  if (!restaurant) {
    throw new Error(error || 'Restaurant not available');
  }

  return restaurant;
}
